import zod from 'zod';

// Imported from the geometry-free `visionConfigSchemas` leaf rather than
// `LocateTypes`/`VisionInterface`, which transitively pull in
// `@sb/geometry`/`three`. `@sb/ai` is consumed by lightweight backend services
// (e.g. updater-bot) that do not bundle `three`. See visionConfigSchemas.ts.
import {
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
  RegionOfInterest,
  ResultOrdering,
} from '@sb/routine-runner/vision/visionConfigSchemas';

// Model Architecture Model Types
export const MODEL_TYPE_VALUES = ['umi', 'small', 'rdt2', 'rlt'] as const;
export type ModelType = (typeof MODEL_TYPE_VALUES)[number];

// ! WARNING When adding new data types, must also update RecorderEquipmentTypes in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this equipment type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareEquipmentType = zod.enum([
  'robot',
  'gripper',
  'camera',
  'force_sensor',
  'depth_sensor',
]);
export type BotHardwareEquipmentType = zod.infer<
  typeof BotHardwareEquipmentType
>;

// ! WARNING When adding new data types, must also update RecorderDataType in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this data type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareDataType = zod.enum([
  'pose',
  'joint_states',
  'joint_desires',
  'teleop_command',
  'gripper',
  'force_hardware',
  'video',
  'depth_image',
]);
export type BotHardwareDataType = zod.infer<typeof BotHardwareDataType>;

// Raw artifact this hardware delivers at recording time — the upload-facing
// counterpart of `dataType`. `dataType` says what KIND of stream the hardware
// records (video, pose, …); `rawArtifactType` says what FILE actually arrives
// for the skills pipeline (a `video` camera uploads an mp4 → raw_rgb_video,
// an Insta360 uploads a .insv container → insv). Values mirror the pipeline's
// hardware-recordable raw-input artifact vocabulary (api-srv stageRegistry
// RAW_INPUT_TYPES minus the session-level session_metadata). Null for hardware
// that records no pipeline raw input (force_hardware).
export const RawArtifactType = zod.enum([
  'raw_rgb_video',
  'raw_depth_video',
  'pose',
  'gripper',
  'joint_states',
  'joint_desires',
  'teleop_command',
  'insv',
]);
export type RawArtifactType = zod.infer<typeof RawArtifactType>;

export const BotHardware = zod.object({
  id: zod.string(),
  name: zod.string(),
  equipmentType: BotHardwareEquipmentType,
  dataType: BotHardwareDataType,
  rawArtifactType: RawArtifactType.nullable().optional(),
  metadata: zod.any().nullable(),
  archived: zod.boolean().default(false),
  created: zod.date().optional(),
  customer: zod.string().optional(),
});
export type BotHardware = zod.infer<typeof BotHardware>;
// Pre-parse shape (defaults not yet applied — `archived` optional). The
// contract for callers that assemble hardware rows locally (e.g. the
// demonstration wizard's synthesized pose/gripper slots) before a zod parse
// applies the defaults.
export type BotHardwareInput = zod.input<typeof BotHardware>;

export const HardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(
        zod.string(),
        zod.object({
          id: zod.string(),
        }),
      ),
    }),
  ),
});
export type HardwareConfigSchema = zod.infer<typeof HardwareConfigSchema>;

// Positioning-skill slot keys — shared with api-srv, the wizard, and echo-bot's recorder.
export const POSITIONING_SKILL_PRIMARY_ROBOT_KEY = 'primary';
export const POSITIONING_SKILL_POSE_HARDWARE_KEY = 'pose';
export const POSITIONING_SKILL_JOINT_STATES_HARDWARE_KEY = 'joint_states';
export const POSITIONING_SKILL_CAMERA_HARDWARE_KEYS = [
  'primary_camera',
  'secondary_camera',
  'tertiary_camera',
] as const;
export const POSITIONING_SKILL_MAX_CAMERAS =
  POSITIONING_SKILL_CAMERA_HARDWARE_KEYS.length;

// A hardware-key assignment: a bare equipment ID (legacy shorthand, still
// accepted so persisted routines keep parsing), or an object carrying an
// optional lens `feed` for dual-lens cameras (e.g. Insta360 front/back).
// Mirrored in `libs/feathers-types/skill.ts`'s
// `equipmentAssignmentValueSchema` (that lib can't import `@sb/ai/types` —
// keep the two unions textually aligned).
export const equipmentAssignmentValue = zod.union([
  zod.string(),
  zod.object({
    equipmentId: zod.string(),
    feed: zod.enum(['front', 'back']).optional(),
  }),
]);
export type EquipmentAssignmentValue = zod.infer<
  typeof equipmentAssignmentValue
>;

// The normalized object shape a hardware-key assignment always expands to
// (see `toEquipmentAssignment` in `libs/ai/helpers.ts`) — the object variant
// of `EquipmentAssignmentValue`, with the legacy bare-string shorthand
// already resolved to `equipmentId`.
export type EquipmentAssignment = {
  equipmentId: string;
  feed?: 'front' | 'back';
};

export const HardwareConfigEquipmentMapping = zod.record(
  zod.string(),
  zod.object({
    robotId: zod.string().optional(), // only required for non-primary robots
    robotEquipmentId: zod.string().optional(), // only required for non-primary robots
    hardwareKeyToEquipmentId: zod.record(
      zod.string(),
      equipmentAssignmentValue,
    ),
  }),
);
export type HardwareConfigEquipmentMapping = zod.infer<
  typeof HardwareConfigEquipmentMapping
>;

export const HydratedHardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(zod.string(), BotHardware),
    }),
  ),
});
export type HydratedHardwareConfigSchema = zod.infer<
  typeof HydratedHardwareConfigSchema
>;

const ParsedHardwareConfigEntry = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: HardwareConfigSchema,
});

const ParsedPolicyConfigEntry = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: zod.record(zod.string(), zod.any()),
});

const ParsedInferenceConfig = zod.object({
  obsRes: zod.array(zod.number()).optional(),
  obsHorizon: zod.number().optional(),
  frequency: zod.number().optional(),
  stepsPerInference: zod.number().optional(),
});

const ParsedModelArchitecture = zod.object({
  id: zod.string(),
  name: zod.string(),
  modelType: zod.string(),
  inputHardwareConfigId: zod.string(),
  outputHardwareConfigId: zod.string(),
  inputHardwareConfig: ParsedHardwareConfigEntry.optional(),
  outputHardwareConfig: ParsedHardwareConfigEntry.optional(),
  policyConfigId: zod.string().nullable().optional(),
  inferenceConfigId: zod.string().nullable().optional(),
  policyConfig: ParsedPolicyConfigEntry.nullable().optional(),
  inferenceConfig: ParsedInferenceConfig.nullable().optional(),
});

const ParsedModelMetadata = zod.object({
  id: zod.string(),
  created: zod.string(),
  finished: zod.string().nullable(),
  name: zod.string(),
  trainingDataProjectId: zod.string().nullable(),
  modelArchitecture: ParsedModelArchitecture,
  status: zod.string(),
  epoch: zod.number(),
  dockerImageTag: zod.string().nullable(),
  hyperparameters: zod.record(zod.string(), zod.any()).nullable(),
  provider: zod.string().nullable(),
  neptuneUrl: zod.string().nullable(),
  logUrl: zod.string().nullable(),
  notes: zod.string().nullable(),
  gpuCount: zod.number().nullable(),
  gpuType: zod.string().nullable(),
  costPerHour: zod.number().nullable(),
  inferenceConfig: ParsedInferenceConfig.optional(),
});

export const ParsedModelMetadataPayload = zod.object({
  trainingRun: ParsedModelMetadata.optional(),
  botHardware: zod.record(zod.string(), BotHardware),
});

export const ParsedModel = zod.object({
  modelFilenames: zod.array(zod.string()),
  modelMetadata: ParsedModelMetadataPayload.optional(),
  modelDirectory: zod.string(),
});

export type ParsedModel = zod.infer<typeof ParsedModel>;

// Processor Settings
export const ZarrSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
});
export type ZarrSettings = zod.infer<typeof ZarrSettings>;

export const McapSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
  exportUrdf: zod.boolean().nullable().optional().default(true),
});
export type McapSettings = zod.infer<typeof McapSettings>;

export const SlamSettings = zod.object({
  enabled: zod.boolean().default(false),
  // YAMLs come back as null from the GraphQL response when unset on the
  // stored JSONB. echo-bot's auto-trigger doesn't propagate them through
  // settings (dpj reads from metadata.json directly), so the typical
  // stored shape is {enabled: true} with both YAMLs absent.
  calibrationYaml: zod.string().nullable().optional(),
  imuYaml: zod.string().nullable().optional(),
  // Opt-in two-pass cam-IMU hand-eye refinement (Park-Martin AX=XB).
  // No-op unless the session also has a paired GT recording linked via
  // `metadata.gt_session_id` and a seed `tFlangeCamYaml`.
  refineExtrinsics: zod.boolean().nullable().optional(),
});
export type SlamSettings = zod.infer<typeof SlamSettings>;

export const ProcessorSettings = zod.object({
  greenscreenProcessing: zod.boolean(),
  videoCompression: zod.boolean(),
  autoChunkOnMovement: zod.boolean().default(true),
  zarr: ZarrSettings,
  mcap: McapSettings,
  slam: SlamSettings.nullable().optional(),
});
export type ProcessorSettings = zod.infer<typeof ProcessorSettings>;

export const DEFAULT_PROCESSOR_SETTINGS: ProcessorSettings = {
  greenscreenProcessing: false,
  videoCompression: true,
  autoChunkOnMovement: true,
  zarr: {
    enabled: true,
    timestampSync: true,
  },
  mcap: {
    enabled: true,
    timestampSync: false,
    exportUrdf: true,
  },
  slam: {
    enabled: false,
  },
};

// Inference Config - used by skills-bot inference service
export const InferenceConfigSchema = zod
  .object({
    // Inference env config - set up in ConfigureInferenceRequest once per routine
    obs_res: zod.array(zod.number()).optional(),
    obs_horizon: zod.number(),
    robot_config: zod.record(zod.string(), zod.any()).optional(),
    // Runtime config - sent via StartInferenceRequest, can be overridden per-step
    steps_per_inference: zod.number(),
    frequency: zod.number(),
  })
  .passthrough(); // allow unknown fields to propagate
export type InferenceConfigSchema = zod.infer<typeof InferenceConfigSchema>;

// Policy Config - Hydra training config for diffusion/MLP policies
// This is a flexible schema since policy configs vary significantly between policy types
export const PolicyConfigSchema = zod
  .object({
    name: zod.string().optional(),
    _target_: zod.string(), // Workspace class target
    task_name: zod.string().optional(),
    shape_meta: zod.any().optional(),
    exp_name: zod.string().optional(),
    save_aws: zod.boolean().optional(),
    eval_test_set: zod.boolean().optional(),
    n_action_steps: zod.number().optional(),
    enabled_completion_prediction: zod.boolean().optional(),
    enabled_status_prediction: zod.boolean().optional(),
    enabled_goal_pose_prediction: zod.boolean().optional(),
    policy: zod.record(zod.string(), zod.any()), // Policy-specific config (varies by type)
    task: zod.record(zod.string(), zod.any()).optional(), // Task/dataset config (e.g. dataset.completion_distance_threshold)
    ema: zod.record(zod.string(), zod.any()).optional(),
    dataloader: zod.record(zod.string(), zod.any()).optional(),
    val_dataloader: zod.record(zod.string(), zod.any()).optional(),
    test_dataloader: zod.record(zod.string(), zod.any()).optional(),
    optimizer: zod.record(zod.string(), zod.any()).optional(),
    training: zod.record(zod.string(), zod.any()).optional(),
    logging: zod.record(zod.string(), zod.any()).optional(),
    checkpoint: zod.record(zod.string(), zod.any()).optional(),
    multi_run: zod.record(zod.string(), zod.any()).optional(),
    hydra: zod.record(zod.string(), zod.any()).optional(),
    defaults: zod.array(zod.any()).optional(),
  })
  .passthrough(); // allow unknown fields to propagate
export type PolicyConfigSchema = zod.infer<typeof PolicyConfigSchema>;

// ---------------------------------------------------------------------------
// Skill types — discriminated union keyed on `type`
// ---------------------------------------------------------------------------

export const SkillType = zod.enum([
  'control',
  'locate',
  'classify',
  'agent',
  // Demonstration / show-path skills: skillType is the broad motion category;
  // the path-by-demonstration behavior is the `method` ('show-path').
  'ai-motion',
]);
export type SkillType = zod.infer<typeof SkillType>;

const BaseSkillSchema = zod.object({
  id: zod.string(),
  name: zod.string(),
  description: zod.string().nullable(),
  icon: zod.string().nullable().default(null),
  skillType: SkillType,
  method: zod.string(),
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
  createdAt: zod.string(),
  updatedAt: zod.string(),
});

// Control skill — fully defined

// Tracks which model checkpoint is active / downloaded on the robot
export const ControlModelVersion = zod.object({
  trainingRunId: zod.string(),
  // epoch / checkpointPath are null until the training run reports them; the
  // versions tab writes `run.epoch ?? null` when activating a ref.
  epoch: zod.number().int().nullable(),
  checkpointPath: zod.string().nullable(),
});
export type ControlModelVersion = zod.infer<typeof ControlModelVersion>;

export const ControlSkillMethod = zod.enum([
  'small_model',
  // 'foundation_model',
  // 'partner_model',
]);
export type ControlSkillMethod = zod.infer<typeof ControlSkillMethod>;

// TODO: decide if we want to merge into ParsedInferenceConfig (keeping separate for now)
export const ControlInferenceConfig = zod
  .object({
    frequency: zod.number().optional(),
    obsHorizon: zod.number().optional(),
    obsRes: zod.array(zod.number()).optional(),
    stepsPerInference: zod.number().optional(),
  })
  .passthrough();
export type ControlInferenceConfig = zod.infer<typeof ControlInferenceConfig>;

// Shared config for Control skills: Marvin links, checkpoint ref, inference
// knobs, camera setup. Per-method tuning lives in `methodConfig`.
export const SharedControlSkillConfig = zod.object({
  cameraCount: zod.union([zod.literal(1), zod.literal(2)]).default(2),
  taskId: zod.string().nullable().default(null),
  projectId: zod.string().nullable().default(null),
  activeModelRef: ControlModelVersion.nullable().default(null),
  modelVersions: zod.array(ControlModelVersion).default([]),
  inferenceConfig: ControlInferenceConfig.default({}),
  modelArchitectureId: zod.string().nullable().default(null),
});
export type SharedControlSkillConfig = zod.infer<
  typeof SharedControlSkillConfig
>;

export const ControlSkill = BaseSkillSchema.extend({
  skillType: zod.literal('control'),
  method: ControlSkillMethod,
  shared: SharedControlSkillConfig,
  // Method-specific runtime config (e.g. diffusion steps, noise schedule for
  // small_model). Shape TBD — using a passthrough record until we know what
  // each method needs.
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ControlSkill = zod.infer<typeof ControlSkill>;

// Locate skill

// Shared envelope for every Locate skill — pose, camera, and projection.
// Per-method tuning knobs (positives/negatives, thresholds, GPU runner) live in
// the method-specific config below.

export const PoseSchema = zod.object({
  kind: zod.enum(['tooltip', 'joint']).default('tooltip'),
  x: zod.number(),
  y: zod.number(),
  z: zod.number(),
  roll: zod.number(),
  pitch: zod.number(),
  yaw: zod.number(),
  // Real arm joint angles (radians, J0..J5) captured alongside the Cartesian
  // pose. Optional because vision-derived poses (plane corners, detections)
  // have no joint solution; a captured robot pose persists them so the saved
  // skill can return to the exact taught arm configuration rather than an IK
  // re-solve of the tooltip pose. The 6-tuple is spelled out inline (rather
  // than via `@sb/utilities`' `six`) so `ai/types` imports nothing from the
  // `@sb/utilities` barrel — that barrel eagerly pulls in `eventemitter3` via
  // `EventEmitter`, which lightweight bundled consumers (e.g. updater-bot) do
  // not ship. Structurally matches `ArmJointPositions`, so consumers need no
  // cast.
  jointAngles: zod
    .tuple([
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
    ])
    .optional(),
});
export type PoseSchema = zod.infer<typeof PoseSchema>;

export const ProjectionPlane = zod.object({
  origin: PoseSchema,
  xAxis: PoseSchema,
  yAxis: PoseSchema,
});
export type ProjectionPlane = zod.infer<typeof ProjectionPlane>;

export const ProjectionConfig = zod.object({
  // How 2D detections become 3D poses. 'plane' ray-casts onto a taught/linked
  // plane; 'detection3d' uses the camera depth stream + the shared
  // Detection3D pose-estimation backend. Defaults to 'plane' (the only
  // pre-existing method; there is no 'singlePose').
  method: zod.enum(['plane', 'detection3d']).default('plane'),
  // Plane-method fields (ignored when method === 'detection3d'). A freshly-taught
  // plane is stored inline (origin + X-axis + Y-axis poses); mutually exclusive
  // with planeSpaceItemId. `null` when not yet configured.
  plane: ProjectionPlane.nullable().default(null),
  // Set instead of `plane` when linking an existing global Space plane by ID.
  planeSpaceItemId: zod.string().nullable().default(null),
  // Object height above the reference plane, in mm (plane method only). At
  // detection time the projection plane is offset along its normal by this
  // much before ray-casting, so match poses land on the object's top surface
  // instead of the plane. Absent/null on older skills = 0.
  objectHeight: zod.number().nullable().optional(),
  // detection3d-method config (null unless method === 'detection3d'). Reuses
  // the shared `Detection3DPoseEstimationConfig` (defined in visionConfigSchemas;
  // also extended by the routine Locate step's Detection3DParams).
  detection3d: Detection3DPoseEstimationConfig.nullable().default(null),
  transform: zod
    .object({
      // Grasp frame: defines the base in-plane orientation AND the frame the
      // X/Y/Z + roll/pitch/yaw offset rides in. 'object' = detection principal
      // axis (default), 'plane' = plane X, 'world' = world X on the plane.
      graspFrame: zod.enum(['object', 'plane', 'world']).default('object'),
      x: zod.number().default(0), // mm
      y: zod.number().default(0), // mm
      z: zod.number().default(0), // mm
      roll: zod.number().default(0), // deg
      pitch: zod.number().default(0), // deg
      yaw: zod.number().default(0), // deg
    })
    .nullable()
    .default(null),
});
export type ProjectionConfig = zod.infer<typeof ProjectionConfig>;

export const MatchesConfig = zod.object({
  limitResults: zod.number().int().min(1).max(100).default(50),
  orderBy: ResultOrdering.default('top-left-to-bottom-right'),
  // When true (default), each match is refined with a SAM2 segmentation pass
  // for a pixel-accurate mask + tighter box; when false, the heatmap-patch
  // bounding box is used directly. Maps to DinoFastParams.enableSegmentationMask.
  enableSegmentationMask: zod.boolean().default(true),
  // Which segmentation backend refines OBBs into masks when
  // enableSegmentationMask is on. Maps to DinoFastParams.segmentationMethod.
  segmentationMethod: zod
    .enum(['sam2', 'guided_filter', 'bilateral_solver', 'heatmap_threshold'])
    .default('heatmap_threshold'),
});
export type MatchesConfig = zod.infer<typeof MatchesConfig>;

export const SharedLocateSkillConfig = zod.object({
  // A freshly-captured training pose is stored inline; selecting an existing
  // global Space position links it via `capturePoseSpaceItemId` instead.
  // Exactly one is set once the Camera & Capture Pose step is complete.
  capturePose: PoseSchema.nullable().default(null),
  capturePoseSpaceItemId: zod.string().nullable().default(null),
  cameraId: zod.string(),
  regionOfInterest: RegionOfInterest.optional(),
  matches: MatchesConfig.default({
    limitResults: 50,
    orderBy: 'top-left-to-bottom-right',
    enableSegmentationMask: true,
    segmentationMethod: 'heatmap_threshold',
  }),
  projection: ProjectionConfig,
  // Name of the Space position list that locate results write to at runtime.
  // Drives the routine-runner Locate step's `positionListName` argument.
  positionListName: zod.string().default('Locate results'),
  /**
   * Storage id of the taught snapshot — a JPEG of the camera frame with the
   * detection overlays baked in, captured when teaching is confirmed. Shown on
   * the skill detail General tab. Absent on skills taught before this existed
   * or whose teach produced no result; the General tab then shows an empty
   * state. Not read by the locate runtime.
   */
  snapshotStorageID: zod.string().nullable().optional(),
  orientationReference: zod
    .object({
      // Normalized reference contour: array of [x, y] points.
      referenceContour: zod.array(zod.array(zod.number()).length(2)),
    })
    .nullable()
    .default(null),
  /**
   * Why teach produced no `orientationReference`, when it tried and failed.
   * Null when a reference exists, or when none was ever attempted.
   *
   * Persisted only so the Projection step can say which failure happened: a
   * `symmetric` part can never carry an object frame, while a
   * `sceneInconsistent` one usually just needs better lighting or a tighter
   * ROI before re-teaching. Both leave `orientationReference` null, so the
   * reason cannot be recovered from it. Not read by the locate runtime.
   */
  orientationReferenceRejection: zod
    .enum(['symmetric', 'sceneInconsistent'])
    .nullable()
    .default(null),
});
export type SharedLocateSkillConfig = zod.infer<typeof SharedLocateSkillConfig>;

// Serialized weights for the learned locate scorer. Mirror of routine-runner's
// `DinoFastLocateHead`, trained server-side by vision-bot. When present this
// replaces the cosine scorer; when null, locate falls back to cosine. `kind`
// selects the model: 'mlp' (single-hidden-layer net) or 'logistic' (linear).
const DinoFastMlpHeadWeights = zod.object({
  kind: zod.literal('mlp'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  hidden: zod.number().int(),
  w1: zod.array(zod.number()), // hidden * inputDim, row-major [h][i]
  b1: zod.array(zod.number()), // hidden
  w2: zod.array(zod.number()), // hidden
  b2: zod.number(),
});

const DinoFastLogisticHeadWeights = zod.object({
  kind: zod.literal('logistic'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  w: zod.array(zod.number()), // inputDim
  b: zod.number(),
});

export const DinoFastLocateHead = zod.discriminatedUnion('kind', [
  DinoFastMlpHeadWeights,
  DinoFastLogisticHeadWeights,
]);

export type DinoFastLocateHead = zod.infer<typeof DinoFastLocateHead>;

// Upper bound of the ClickFind match-area range, in original-image px²; also the
// "unbounded" sentinel (a configured max at/above this disables max-area
// filtering). Redefined here (rather than imported from `@sb/dinofast`) because
// `@sb/ai` is bundled into lightweight backend services (e.g. api-srv,
// updater-bot) and deliberately avoids pulling in `@sb/dinofast`'s tensor/array
// export surface. Keep in sync with `CLICKFIND_MAX_MATCH_AREA_PX2` in
// `libs/dinofast/src/minMatchArea.ts`.
const CLICKFIND_MAX_MATCH_AREA_PX2 = 50000;

export const DinoFastSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  embeddingModelVersion: zod.string().default('dinov3-vits16-v1'),
  embeddingDim: zod.number().int().default(384),
  precision: DinoFastPrecision.default('standard'),
  positives: zod.array(DinoFastReferenceEmbedding).default([]),
  negatives: zod.array(DinoFastReferenceEmbedding).default([]),
  // Learned scorer trained in the teach UI. Null on legacy/untaught steps,
  // which fall back to cosine scoring at runtime.
  locateHead: DinoFastLocateHead.nullable().default(null),
  scoreThreshold: zod.number().min(-2).max(2).default(0.5),
  // Minimum match area in original-image pixel² space. Matches whose final
  // shape (contour when segmented, else the detector box) falls below this are
  // discarded at match time in runLocate.
  minMatchAreaPx2: zod.number().min(0).default(2000),
  // Maximum match area in original-image pixel² space. Matches whose final
  // shape exceeds this are discarded. Defaults to (and is capped at)
  // CLICKFIND_MAX_MATCH_AREA_PX2, which the runtime treats as "no upper limit".
  // Skills saved before this field existed parse to the default → unbounded.
  maxMatchAreaPx2: zod.number().min(0).default(CLICKFIND_MAX_MATCH_AREA_PX2),
  nmsIouThreshold: zod.number().min(0).max(1).default(0.3),
  maxBlobs: zod.number().int().min(1).default(20),
});
export type DinoFastSkillConfig = zod.infer<typeof DinoFastSkillConfig>;

// Mirrors the runner's `Blob2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field, with identical
// defaults. Keep the two in sync; a divergence would surface as a parse error
// at locate time.
export const BlobSkillConfig = zod.object({
  minArea: zod.number().default(1000),
  maxArea: zod.number().default(10000),
  minThreshold: zod.number().default(50),
  maxThreshold: zod.number().default(220),
  minCircularity: zod.number().default(0.1),
  maxCircularity: zod.number().default(1),
  filterByCircularity: zod.boolean().default(false),
  invertImage: zod.boolean().default(false),
  disableRotation: zod.boolean().default(false),
});
export type BlobSkillConfig = zod.infer<typeof BlobSkillConfig>;

// Mirrors the runner's `Shape2DParams` + `TemplateImage`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field, with identical
// defaults. This is a FLAT config: `templateImage` is a peer key alongside the
// Shape2DParams fields — exactly the shape `mapSkillMethodToLocateMethod`'s
// `shape_match` branch reads. Keep the two in sync; a divergence would surface
// as a parse error at locate time.
export const ShapeMatchSkillConfig = zod.object({
  minScale: zod.number().default(0.8),
  maxScale: zod.number().default(1.2),
  maxRotation: zod.number().default(180),
  matchThreshold: zod.number().default(0.5),
  boundingBox: zod
    .object({
      rotationDegrees: zod.number().default(0),
      scaleX: zod.number().default(1),
      scaleY: zod.number().default(1),
    })
    .default({}),
  filterIntersections: zod.boolean().default(true),
  disableRotation: zod.boolean().default(false),
  rejectOnEdge: zod.boolean().default(false),
  // Peer key (mirrors `TemplateImage`), not nested under a `settings` object.
  templateImage: zod.object({ storageID: zod.string() }).default({
    storageID: '',
  }),
});
export type ShapeMatchSkillConfig = zod.infer<typeof ShapeMatchSkillConfig>;

// Mirrors the runner's `TextPromptDetection2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field. Keep the two
// in sync; the only intentional divergence is the `.min(0).max(1)` bound on
// `confidenceThreshold` here.
export const TextPromptSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  textInput: zod.string().default(''),
  useSam: zod.boolean().default(false),
  confidenceThreshold: zod.number().min(0).max(1).default(0.35),
});
export type TextPromptSkillConfig = zod.infer<typeof TextPromptSkillConfig>;

// Mirrors the runner's `CustomCheckpointModelType`
// (`libs/routine-runner/vision/LocateTypes.ts`). Redefined here (rather than
// imported) because `@sb/ai` deliberately avoids importing from `LocateTypes`,
// which transitively pulls in `@sb/geometry`/`three`. Keep the two enums in
// sync; a divergence would surface as a parse error at locate time.
export const CustomCheckpointModelType = zod.enum([
  'YoloV11',
  'YoloV8-OBB',
  'RF-DETR-Nano',
  'RF-DETR-Small',
  'RF-DETR-Medium',
  'RF-DETR-Large',
]);
export type CustomCheckpointModelType = zod.infer<
  typeof CustomCheckpointModelType
>;

// Mirrors the runner's `CustomCheckpointDetection2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field. The runtime
// params require `gpuTaskRunnerId`/`modelPath` (no default); this skill config
// defaults them to '' so the wizard can start empty and gate Continue via
// `validateCustomCheckpointTeach`. `mapSkillMethodToLocateMethod` re-parses
// against the strict runtime schema at locate time. Keep the two in sync.
export const CustomCheckpointSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  modelPath: zod.string().default(''),
  modelType: CustomCheckpointModelType.default('YoloV11'),
  thresholdingParams: zod.record(zod.string(), zod.string()).default({
    confidence_threshold: '0.3',
    nms_threshold: '0.4',
    max_detections: '100',
  }),
  // Model source: 'checkpoint' (a runner-hosted checkpoint file, the default)
  // or 'roboflow' (a Roboflow-hosted model). The roboflow* fields are
  // UI + persistence only — the locator does not read them yet (see
  // docs/plans/2026-07-14-roboflow-locate-model-source-design.md).
  modelSource: zod.enum(['checkpoint', 'roboflow']).default('checkpoint'),
  // Model url "<workspace>/<project>/<version>".
  roboflowId: zod.string().optional(),
  roboflowDisplayName: zod.string().optional(),
  roboflowProjectSlug: zod.string().optional(),
  roboflowWorkspaceName: zod.string().optional(),
});
export type CustomCheckpointSkillConfig = zod.infer<
  typeof CustomCheckpointSkillConfig
>;

export const LocateSkillMethod = zod.enum([
  'dino_fast', // UI label: "ClickFind AI"
  'blob',
  'shape_match',
  'text_prompt',
  'custom_checkpoint',
  'mock',
]);
export type LocateSkillMethod = zod.infer<typeof LocateSkillMethod>;

export const LocateSkill = BaseSkillSchema.extend({
  skillType: zod.literal('locate'),
  method: LocateSkillMethod,
  shared: SharedLocateSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type LocateSkill = zod.infer<typeof LocateSkill>;

// Classify skill — stub (config schemas deferred)

export const ClassifySkillMethod = zod.enum(['template_match']);
export type ClassifySkillMethod = zod.infer<typeof ClassifySkillMethod>;

// Placeholder until the classify wizard work begins.
export const SharedClassifySkillConfig = zod.object({}).passthrough();
export type SharedClassifySkillConfig = zod.infer<
  typeof SharedClassifySkillConfig
>;

export const ClassifySkill = BaseSkillSchema.extend({
  skillType: zod.literal('classify'),
  method: ClassifySkillMethod,
  shared: SharedClassifySkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ClassifySkill = zod.infer<typeof ClassifySkill>;

// Agent skill — free-form instruction text for domain context

export const AgentSkillMethod = zod.enum(['instructions']);
export type AgentSkillMethod = zod.infer<typeof AgentSkillMethod>;

export const SharedAgentSkillConfig = zod.object({
  instructions: zod.string().default(''),
});
export type SharedAgentSkillConfig = zod.infer<typeof SharedAgentSkillConfig>;

export const AgentSkill = BaseSkillSchema.extend({
  skillType: zod.literal('agent'),
  method: AgentSkillMethod,
  shared: SharedAgentSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type AgentSkill = zod.infer<typeof AgentSkill>;

// Show-path (demonstration playback) skill

export const ShowPathSkillMethod = zod.enum(['show-path']);
export type ShowPathSkillMethod = zod.infer<typeof ShowPathSkillMethod>;

// Permissive envelope — the concrete shape (recordings, referenceFrame, …)
// is validated by `DemonstrationSkillConfig` in `@sb/feathers-types`, which
// can't be imported here without creating a dependency cycle.
export const SharedShowPathSkillConfig = zod.object({}).passthrough();
export type SharedShowPathSkillConfig = zod.infer<
  typeof SharedShowPathSkillConfig
>;

export const ShowPathSkill = BaseSkillSchema.extend({
  skillType: zod.literal('ai-motion'),
  method: ShowPathSkillMethod,
  shared: SharedShowPathSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ShowPathSkill = zod.infer<typeof ShowPathSkill>;

// Discriminated union

export const Skill = zod.discriminatedUnion('skillType', [
  ControlSkill,
  LocateSkill,
  ClassifySkill,
  AgentSkill,
  ShowPathSkill,
]);
export type Skill = zod.infer<typeof Skill>;

const toSkillInput = <
  T extends
    | typeof ControlSkill
    | typeof LocateSkill
    | typeof ClassifySkill
    | typeof AgentSkill
    | typeof ShowPathSkill,
>(
  variant: T,
) =>
  variant
    .omit({ id: true, createdAt: true, updatedAt: true, description: true })
    .extend({ description: zod.string().nullable().optional() });

export const SkillInput = zod.discriminatedUnion('skillType', [
  toSkillInput(ControlSkill),
  toSkillInput(LocateSkill),
  toSkillInput(ClassifySkill),
  toSkillInput(AgentSkill),
  toSkillInput(ShowPathSkill),
]);
export type SkillInput = zod.infer<typeof SkillInput>;
